import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

// context
import { BreadcrumbProvider } from "./context/BreadcrumbContext";

import Layout from "./components/PageNavigation/Layout";
import "bootstrap/dist/css/bootstrap.min.css";
import theme from "./Theme";
import { Box, CircularProgress, ThemeProvider } from "@mui/material";
import PrivateRoutes from "./utils/PrivateRoutes";
import { LogisticsUpdateLauncherProvider } from "./features/Logistics/context/LogisticsUpdateLauncher";
import { AuthProvider } from "./context/AuthContext";
import ContentTypeInitializer from "./components/ContentTypeInitializer";
import { FaultProvider } from "./context/FaultContext";
import Instruments from "./components/Assets/Instruments";

// routes
import { maintenanceGuideRoutes } from "./features/maintenanceGuide/maintenanceGuideRoutes";
import { logisticsRoutes } from "./features/Logistics/logisticsRoutes";
import {
  KnowledgeContinuePage,
  KnowledgeExtractStartPage,
  KnowledgeExtractionWorkspacePage,
  KnowledgeManualExtractionPage,
  KnowledgeHomePage,
  KnowledgeOldAddressRedirect,
  KNOWLEDGE_BASE_PATH,
  KNOWLEDGE_CONTINUE_PATH,
  KNOWLEDGE_EXTRACT_PATH,
} from "./features/knowledge";

const Sites = lazy(() => import("./components/Assets/Sites"));
const ShowProjects = lazy(() => import("./components/Projects/ShowProjects"));
const ClientDocs = lazy(() => import("./components/Projects/ClientDocuments"));
const ProjectDashboard = lazy(() =>
  import("./components/Projects/ProjectDashboard"),
);
const ProjectDocuments = lazy(() =>
  import("./components/Projects/ProjectDocuments"),
);
const ProjectProgress = lazy(() =>
  import("./components/Projects/ProjectProgress"),
);
const ShowDeliverables = lazy(() =>
  import("./components/Deliverables/ShowDeliverables"),
);
const ProjectEquipment = lazy(() =>
  import("./components/Projects/ProjectEquipment"),
);
const AddProject = lazy(() => import("./components/Projects/AddProject"));
const AddDeliverable = lazy(() =>
  import("./components/Deliverables/AddDeliverable"),
);
const UpdateProject = lazy(() => import("./components/Projects/UpdateProject"));
const Login = lazy(() => import("./components/Auth/Login"));
const Signup = lazy(() => import("./components/Auth/Signup"));
const Activate = lazy(() => import("./components/Auth/Activate"));
const ResetPassword = lazy(() => import("./components/Auth/ResetPassword"));
const ResetPasswordMail = lazy(() =>
  import("./components/Auth/ResetPasswordMail"),
);
const ResetPasswordConfirm = lazy(() =>
  import("./components/Auth/ResetPasswordConfirm"),
);
const NotificationPage = lazy(() =>
  import("./components/InstrumentControl/NotificationsPage"),
);
const PDFReader = lazy(() => import("./components/PDFReader"));
const RecycleBin = lazy(() => import("./components/Recyclebin"));
const MyProfile = lazy(() => import("./components/Auth/MyProfile"));
const ProjectChangeNotification = lazy(() => import("./components/PCN"));
const UpdatePCN = lazy(() => import("./components/UpdatePCN"));
const AddPCN = lazy(() => import("./components/AddPCN"));
const Queries = lazy(() => import("./components/Projects/Queries"));
const Adjudicate = lazy(() => import("./components/Adjudicate"));
const Plants = lazy(() => import("./components/Assets/Plants"));
const EFADashboard = lazy(() => import("./components/EFA/EFADashboard"));
const EFALayout = lazy(() => import("./components/EFALayout"));
const Equipment = lazy(() => import("./components/Assets/Equipment"));
const LogisticsDashboardPage = lazy(() =>
  import("./components/Logistics/LogisticsDashboardPage"),
);
const ReadinessPage = lazy(() => import("./features/readiness/pages/ReadinessPage"));
const RelatedAssets = lazy(() => import("./components/Assets/RelatedAssets"));
const Home = lazy(() => import("./components/Assets/Home"));
const PanelComponents = lazy(() =>
  import("./components/Assets/PanelComponents"),
);
const ShareLandingPage = lazy(() =>
  import("./components/Sharing/ShareLandingPage"),
);
const RecommendedActions = lazy(() =>
  import("./components/Diagnostics/RecommendedActions"),
);
const DocumentsContainer = lazy(() =>
  import("./components/Assets/AssetDocumentsContainer"),
);
const GlobalDocuments = lazy(() =>
  import("./components/Documents/GlobalDocuments"),
);

const RouteFallback = () => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <FaultProvider>
        <BreadcrumbProvider>
          <Router>
            <AuthProvider>
              <ContentTypeInitializer />
              <LogisticsUpdateLauncherProvider>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Routes for authentication */}
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/check-mail" element={<ResetPasswordMail />} />
                <Route path="/signup" element={<Signup />} />

                <Route
                  path="/password/reset/confirm/:uid/:token"
                  element={<ResetPasswordConfirm />}
                />
                <Route
                  path="/activate/:otp_code/:encoded_email"
                  element={<Activate />}
                />

                {/* Private routes, all routes that need loggin in */}
                <Route element={<PrivateRoutes />}>
                  {/* <Route
                path="/"
                element={
                  <Layout>
                    <LandingPage />
                  </Layout>
                }
              /> */}
                  <Route path="/share/:token" element={<ShareLandingPage />} />
                  <Route
                    path="/"
                    element={
                      <Layout>
                        <Home />
                      </Layout>
                    }
                  />
                  <Route
                    path="/design-engineering-docs"
                    element={
                      <Layout>
                        <GlobalDocuments />
                      </Layout>
                    }
                  />
                  <Route
                    path="/sites/:siteId"
                    element={
                      <Layout>
                        <Sites />
                      </Layout>
                    }
                  />
                  <Route
                    path="/adjudicate"
                    element={
                      <Layout>
                        <Adjudicate />
                      </Layout>
                    }
                  />

                <Route
                  path="/projectProgress"
                  element={
                    <Layout>
                      <ProjectProgress />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId"
                  element={
                    <Layout>
                      <Plants />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId"
                  element={
                    <Layout>
                      <Equipment />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId/logistics"
                  element={
                    <Layout>
                      <LogisticsDashboardPage />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/readiness"
                  element={
                    <Layout>
                      <ReadinessPage />
                    </Layout>
                  }
                />
                <Route
                  path="/readiness"
                  element={
                    <Layout>
                      <ReadinessPage />
                    </Layout>
                  }
                />
                <Route
                  path="/faults-diagnostics"
                  element={
                    <Layout>
                      <RecommendedActions />
                    </Layout>
                  }
                />
                <Route
                  path={KNOWLEDGE_BASE_PATH}
                  element={
                    <Layout>
                      <KnowledgeHomePage />
                    </Layout>
                  }
                />
                {/* The Review area (removed 30 October 2026): its old addresses land on the Knowledge Base. */}
                <Route path={`${KNOWLEDGE_BASE_PATH}/review/*`} element={<KnowledgeOldAddressRedirect />} />
                <Route
                  path={KNOWLEDGE_EXTRACT_PATH}
                  element={
                    <Layout>
                      <KnowledgeExtractStartPage />
                    </Layout>
                  }
                />
                <Route
                  path={`${KNOWLEDGE_EXTRACT_PATH}/manual/:fileId`}
                  element={
                    <Layout>
                      <KnowledgeManualExtractionPage />
                    </Layout>
                  }
                />
                <Route
                  path={`${KNOWLEDGE_EXTRACT_PATH}/:sessionId`}
                  element={
                    <Layout>
                      <KnowledgeExtractionWorkspacePage />
                    </Layout>
                  }
                />
                <Route
                  path={KNOWLEDGE_CONTINUE_PATH}
                  element={
                    <Layout>
                      <KnowledgeContinuePage />
                    </Layout>
                  }
                />
                {/* A row's address from before the Review area: the Knowledge Base too. */}
                <Route path={`${KNOWLEDGE_BASE_PATH}/:candidateId`} element={<KnowledgeOldAddressRedirect />} />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId/faults-diagnostics"
                  element={
                    <Layout>
                      <RecommendedActions />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId/sub-asset/:subAssetId/faults-diagnostics"
                  element={
                    <Layout>
                      <RecommendedActions />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId/sub-asset/:subAssetId/components/:componentId/faults-diagnostics"
                  element={
                    <Layout>
                      <RecommendedActions />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId/sub-asset/:subAssetId"
                  element={
                    <Layout>
                      <PanelComponents />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId/documents"
                  element={
                    <Layout>
                      <DocumentsContainer />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId/sub-asset/:subAssetId/documents"
                  element={
                    <Layout>
                      <DocumentsContainer />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:assetId/sub-asset/:subAssetId/components/:componentId"
                  element={
                    <Layout>
                      <Instruments />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/project-dashboard/:projectId/:projectType/assets"
                  element={
                    <Layout>
                      <ProjectEquipment />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/plants/:plantId/assets/:id/efa"
                  element={
                    <Layout>
                      <EFADashboard />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/projects"
                  element={
                    <Layout>
                      <ShowProjects />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/project-dashboard/:projectId/:projectType/clientdocuments"
                  element={
                    <Layout>
                      <ClientDocs />
                    </Layout>
                  }
                />
                <Route
                  path="/projectchangenotification"
                  element={
                    <Layout>
                      <ProjectChangeNotification />
                    </Layout>
                  }
                />
                <Route
                  path="/pcn/:id/update"
                  element={
                    <Layout>
                      <UpdatePCN />
                    </Layout>
                  }
                />
                <Route
                  path="/pdfviewer"
                  element={
                    <Layout>
                      <PDFReader />
                    </Layout>
                  }
                />
                <Route
                  path="/addPCN"
                  element={
                    <Layout>
                      <AddPCN />
                    </Layout>
                  }
                />
                <Route
                  path="/recyclebin"
                  element={
                    <Layout>
                      <RecycleBin />
                    </Layout>
                  }
                />
                <Route
                  path="/sites/:siteId/project-dashboard/:projectId/:projectType"
                  element={
                    <Layout>
                      <ProjectDashboard />
                    </Layout>
                  }
                />
                <Route
                  path="/showDocuments"
                  element={
                    <Layout>
                      <ProjectDocuments />
                    </Layout>
                  }
                />
                  <Route
                    path="/notifications/:notificationId"
                    element={
                      <Layout>
                        <NotificationPage />
                      </Layout>
                    }
                  />
                  <Route
                    path="/sites/:siteId/project-dashboard/:projectId/:projectType/deliverables"
                    element={
                      <Layout>
                        <ShowDeliverables />
                      </Layout>
                    }
                  />
                  <Route
                    path="/sites/:siteId/project-dashboard/:projectId/:projectType/qaqc"
                    element={
                      <Layout>
                        <EFALayout />
                      </Layout>
                    }
                  />
                  <Route
                    path="/sites/:siteId/project-dashboard/:projectId/queries"
                    element={
                      <Layout>
                        <Queries />
                      </Layout>
                    }
                  />
                  <Route
                    path="/sites/:siteId/addProject"
                    element={
                      <Layout>
                        <AddProject />
                      </Layout>
                    }
                  />
                  <Route
                    path="/sites/:siteId/deliverables/:projectId/addDeliverable"
                    element={
                      <Layout>
                        <AddDeliverable />
                      </Layout>
                    }
                  />
                  <Route
                    path="/sites/:siteId/projects/:id/update/:projectType"
                    element={
                      <Layout>
                        <UpdateProject />
                      </Layout>
                    }
                  />
                  <Route
                    path="/myprofile"
                    element={
                      <Layout>
                        <MyProfile />
                      </Layout>

                    }
                  />

                  {/* Feature route -self managed */}
                   {logisticsRoutes}
                   {maintenanceGuideRoutes}


                </Route>
              </Routes>
              </Suspense>
              </LogisticsUpdateLauncherProvider>
            </AuthProvider>
          </Router>
        </BreadcrumbProvider>
      </FaultProvider>
    </ThemeProvider>
  );
}

export default App;
